import { trace } from '@opentelemetry/api';

type LogLevel = 'info' | 'error' | 'warn' | 'debug';

type LogRecord = {
  timestamp: string;
  message: string;
  level: LogLevel;
  'trace.id'?: string;
  'span.id'?: string;
  'error.type'?: string;
  'error.stack'?: string;
  meta?: Record<string, unknown>;
};

export class Logger {
  private isDev: boolean;

  constructor(private readonly defaultContext = 'App') {
    this.isDev = process?.env?.NODE_ENV === 'development';
  }

  private getSource(context = this.defaultContext): string {
    return context === this.defaultContext
      ? context
      : `${this.defaultContext}/${context}`;
  }

  private getTraceContext(): { 'trace.id'?: string; 'span.id'?: string } {
    if (typeof window !== 'undefined') {
      return {};
    }

    try {
      const span = trace.getActiveSpan();
      const ctx = span?.spanContext();

      if (ctx && trace.isSpanContextValid(ctx)) {
        return { 'trace.id': ctx.traceId, 'span.id': ctx.spanId };
      }
    } catch (error) {
      if (this.isDev) {
        console.debug(
          '[Logger] Failed to read OpenTelemetry trace context',
          error,
        );
      }
    }
    return {};
  }

  private buildRecord(args: {
    message: string;
    level: LogLevel;
    context?: string;
    meta?: Record<string, unknown>;
    errorFields?: { 'error.type'?: string; 'error.stack'?: string };
  }): string {
    const source = this.getSource(args.context);
    const record: LogRecord = {
      level: args.level,
      timestamp: new Date().toISOString(),
      message: `[${source}] ${args.message}`,
      ...this.getTraceContext(),
      ...args.errorFields,
      ...(args.meta && { meta: args.meta }),
    };

    return JSON.stringify(record);
  }

  private printLog(
    message: string,
    context = this.defaultContext,
    level: LogLevel = 'info',
    meta?: Record<string, unknown>,
  ) {
    console[level](this.buildRecord({ message, level, context, meta }));
  }

  public info = (
    message: string,
    context?: string,
    meta?: Record<string, unknown>,
  ) => this.printLog(message, context, 'info', meta);

  public log = (
    message: string,
    context?: string,
    meta?: Record<string, unknown>,
  ) => this.printLog(message, context, 'info', meta);

  public error = (
    err: string | Error,
    context?: string,
    meta?: Record<string, unknown>,
  ) => {
    const message = typeof err === 'string' ? err : (err?.message ?? '');
    const errorFields: { 'error.type'?: string; 'error.stack'?: string } = {};

    if (typeof err !== 'string') {
      if (err?.name) errorFields['error.type'] = err.name;
      const stack = (err as Error & { stack?: string })?.stack;
      if (stack) errorFields['error.stack'] = stack;
    }

    console.error(
      this.buildRecord({ meta, context, message, errorFields, level: 'error' }),
    );
  };

  public warn = (
    message: string,
    context?: string,
    meta?: Record<string, unknown>,
  ) => this.printLog(message, context, 'warn', meta);

  public debug(
    message: string,
    context?: string,
    meta?: Record<string, unknown>,
  ) {
    if (this.isDev) {
      this.printLog(message, context, 'debug', meta);
    }
  }
}
